<script setup>
import { validatePhoneNumber } from '#common/utils/phone'
import { getCheckoutAddressString } from '#common/utils/checkout'
import { validateNoPOBox, pickValueFromPlace } from '#common/utils/address'

defineProps({
  id: {
    type: String,
    required: true,
  },
})
const emit = defineEmits(['commit', 'validation'])
const errors = ref([])
const formValues = ref({})
const validStateMessage = ref('')
const clubGiftingProvider = inject('clubGiftingProvider')

const metadata = useMetadata()
const memberships = useMemberships()
const sectionState = computed(() => memberships.sectionState)
const drawerOpen = computed(() => {
  return sectionState.value.shippingInformation.drawerOpen
})
// NOTE: This was unused, but I wasn't sure if it was still necessary, feel free to remove future developer
// const committedName = computed(() => {
//   return `${sectionState.value.shippingInformation.firstName} ${sectionState.value.shippingInformation.lastName}`
// })
const committedAddress = computed(() => {
  return getCheckoutAddressString(
    sectionState.value.shippingInformation,
    true,
    true,
  )
})
const committedEmail = computed(() => {
  return sectionState.value.shippingInformation.email
})
const committedPhone = computed(() => {
  return sectionState.value.shippingInformation.phone_number
})
const stateOptions = computed(() =>
  _.mapValues(metadata.stateNamesToIds, (v, k) => k),
)
const sectionClasses = computed(() => {
  const classes = ['checkout-details-section']
  if (!drawerOpen.value) {
    classes.push('edit-section')
  }
  return classes
})
function commit() {
  memberships.setSectionState({
    section: 'shippingInformation',
    value: { ...formValues.value, drawerOpen: false },
  })
  emit('commit')
}
function openDrawer() {
  memberships.setSectionDrawerOpen({
    section: 'shippingInformation',
    value: true,
  })
}
function closeDrawer() {
  errors.value = []
  memberships.setSectionDrawerOpen({
    section: 'shippingInformation',
    value: false,
  })
}
function validPhoneNumber({ value }) {
  return validatePhoneNumber(value, false)
}
function notPOBox({ value }) {
  return validateNoPOBox(value, false)
}
function validState({ value }) {
  const stateCode = metadata.stateNamesToCodes[value]
  if (
    metadata.settingsVars.COMPLIANCE_STATE_GROUPS.non_dtc.includes(stateCode)
  ) {
    validStateMessage.value = `Error: Due to state law, we cannot ship to ${value}`
    return false
  }
  if (
    metadata.settingsVars.COMPLIANCE_STATE_GROUPS.private_label_only.includes(
      stateCode,
    )
  ) {
    validStateMessage.value = `Error: We unforunately cannot ship wine subscriptions to ${value}`
    return false
  }
  return true
}
function autoCompletePlaceChanged({ place }) {
  formValues.value.line1 = `${
    pickValueFromPlace(place, 'street_number').short_name
  } ${pickValueFromPlace(place, 'route').long_name}`.trim()
  formValues.value.line4 = pickValueFromPlace(place, 'locality').long_name
  formValues.value.state_name = pickValueFromPlace(
    place,
    'administrative_area_level_1',
  ).long_name
  formValues.value.postcode = pickValueFromPlace(
    place,
    'postal_code',
  ).short_name
}
</script>

<template>
  <CheckoutSection
    :id="id"
    section-title="Giftee's Shipping Address and Information"
    :show-section-number="true"
    :section-number="3"
    :enabled="true"
    :drawer-open="drawerOpen"
    :show-change-link="true"
    :errors="errors"
    change-text="Edit"
    :classes="sectionClasses"
    @drawerOpen="openDrawer"
    @drawerClosed="closeDrawer"
  >
    <template #collapsed>
      <div class="checkout-section-summary">
        <ul>
          <li>{{ committedAddress }}</li>
          <li>{{ committedEmail }}</li>
          <li>{{ committedPhone }}</li>
        </ul>
      </div>
    </template>
    <template #expanded>
      <div class="checkout-section-content">
        <FormKit
          id="shipping-information"
          v-model="formValues"
          type="form"
          :actions="false"
          :errors="clubGiftingProvider.addressFormErrors.value"
          name="shipping-information"
          class="checkout-form"
          @validation="emit('validation')"
          @submit="commit"
        >
          <div class="row">
            <div class="columns medium-6">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-firstname"
                  name="first_name"
                  validation="required"
                  placeholder="First Name"
                  label="First Name"
                  autocomplete="given-name"
                />
              </div>
            </div>
            <div class="columns medium-6">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-lastname"
                  name="last_name"
                  validation="required"
                  placeholder="Last Name"
                  label="Last Name"
                  autocomplete="family-name"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns small-12">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-company"
                  name="line3"
                  placeholder="Company Name (optional)"
                  label="Company Name (optional)"
                  autocomplete="address-line3"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns small-12">
              <div class="floating">
                <MapsAutocompleteAddressInputFK
                  id="input-giftee-address-line-1"
                  name="line1"
                  autocomplete="address-line1"
                  validation="bail|required|notPOBox"
                  :validation-rules="{ notPOBox }"
                  placeholder="Address (We cannot ship to P.O. boxes)"
                  label="Address (No P.O. boxes)"
                  :validation-messages="{
                    required: 'Error: Address is required.',
                    notPOBox: 'Error: We cannot ship to P.O. boxes',
                  }"
                  @onAddressSelected="autoCompletePlaceChanged"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns small-12">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-address-line-2"
                  name="line2"
                  autocomplete="address-line2"
                  placeholder="Apt, suite, etc (Optional)"
                  label="Apt, suite, etc (Optional)"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns medium-4">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-city"
                  name="line4"
                  autocomplete="address-level2"
                  validation="required"
                  placeholder="City"
                  label="City"
                />
              </div>
            </div>
            <div class="columns medium-4">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-state"
                  type="select"
                  input-class="floating__select"
                  :options="stateOptions"
                  name="state_name"
                  validation="required|validState"
                  placeholder=" "
                  label="State"
                  autocomplete="address-level1"
                  :validation-rules="{ validState }"
                  :validation-messages="{
                    validState: validStateMessage,
                  }"
                />
              </div>
            </div>
            <div class="columns medium-4">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-postcode"
                  name="postcode"
                  validation="bail|required|matches:/^\d{5}(?:-\d{4})?$/"
                  placeholder="Zip/Postal Code"
                  label="Zip/Postal Code"
                  :validation-messages="{
                    matches: 'Error: You must input a valid US Zip Code',
                  }"
                  autocomplete="shipping zip-code"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns medium-6 small-12">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-email"
                  name="email"
                  validation="bail|required|email"
                  placeholder="Giftee Email Address"
                  label="Giftee Email Address"
                  autocomplete="email"
                />
              </div>
            </div>
            <div class="columns medium-6 small-12">
              <div class="floating">
                <FloatingInputFK
                  id="input-giftee-phone"
                  name="phone_number"
                  placeholder="Giftee Phone"
                  label="Giftee Phone"
                  validation="bail|required|validPhoneNumber"
                  :validation-rules="{ validPhoneNumber }"
                  :validation-messages="{
                    validPhoneNumber:
                      'Error: Phone number is incorrect, please use this format: (XXX)-XXX-XXXX',
                  }"
                  autocomplete="tel"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns small-12 medium-9 large-6">
              <p class="form-instructions" style="margin-bottom: 1rem">
                Adult signature required for delivery.
              </p>
            </div>
          </div>
          <div class="row">
            <div class="columns small-12 medium-9 large-6">
              <button
                type="submit"
                form="shipping-information"
                class="primary btn-continue left"
              >
                Continue
              </button>
            </div>
          </div>
        </FormKit>
      </div>
    </template>
  </CheckoutSection>
</template>
